<div class="voluntary-donation px-3 pb-3">

  @if (doneText(); as done) {
  <!-- Taken. The form is gone on purpose: the one thing left to do is close. -->
  <div class="text-center py-3">
    <i class="fas fa-circle-check voluntary-donation__tick" aria-hidden="true"></i>
    <p class="mt-3 mb-4">{{ done }}</p>
    <button type="button" class="btn btn-secondary" (click)="close()">Done</button>
  </div>
  } @else {

  <p class="text-center text-muted">
    Thank you for supporting local farms and our most food-insecure neighbors
  </p>

  <!-- Amount -->
  <div class="voluntary-donation__amounts" role="group" aria-label="Donation amount">
    @for (preset of presetAmounts; track preset) {
    <button type="button" class="btn btn-outline-secondary voluntary-donation__amount"
      [class.is-selected]="isPresetSelected(preset)" (click)="selectPreset(preset)">
      ${{ preset }}
    </button>
    }
  </div>

  <div class="mt-3">
    <label class="form-label small fw-medium" for="donationAmount">Or enter an amount</label>
    <input id="donationAmount" type="text" class="form-control form-control-lg" inputmode="decimal"
      placeholder="$0.00" [value]="displayAmount()" (input)="onAmountInput($event)">
  </div>

  <form [formGroup]="form" class="mt-3" novalidate>

    @if (!hasSession()) {
    <!-- Guest. With no account to read these from, the backend needs them to raise the invoice. -->
    <div class="row g-2">
      <div class="col-12">
        <label class="form-label small fw-medium" for="donorEmail">EMAIL*</label>
        <input id="donorEmail" type="email" class="form-control" formControlName="email"
          autocomplete="email" [class.is-invalid]="invalid('email')">
        @if (invalid('email')) {
        <div class="text-danger small">A valid email is required.</div>
        }
      </div>
      <div class="col-6">
        <label class="form-label small fw-medium" for="donorFirstName">FIRST NAME*</label>
        <input id="donorFirstName" type="text" class="form-control" formControlName="firstName"
          autocomplete="given-name" [class.is-invalid]="invalid('firstName')">
        @if (invalid('firstName')) {
        <div class="text-danger small">Required.</div>
        }
      </div>
      <div class="col-6">
        <label class="form-label small fw-medium" for="donorLastName">LAST NAME*</label>
        <input id="donorLastName" type="text" class="form-control" formControlName="lastName"
          autocomplete="family-name" [class.is-invalid]="invalid('lastName')">
        @if (invalid('lastName')) {
        <div class="text-danger small">Required.</div>
        }
      </div>
    </div>
    }

    @if (hasSession() && hasSavedCard()) {
    <!-- A card on file is the one path that sends no card and needs no captcha. -->
    <div class="voluntary-donation__card-choice mt-2">
      <button type="button" class="btn btn-sm" [class.btn-secondary]="!useNewCard()"
        [class.btn-outline-secondary]="useNewCard()" (click)="payWithSavedCard()">
        Use card ending {{ savedCardNumber() }}
      </button>
      <button type="button" class="btn btn-sm" [class.btn-secondary]="useNewCard()"
        [class.btn-outline-secondary]="!useNewCard()" (click)="payWithNewCard()">
        Use another card
      </button>
    </div>
    }

    @if (needsCard()) {
    <div class="row g-2 mt-1">
      <div class="col-12">
        <label class="form-label small fw-medium" for="donationCardNumber">CARD NUMBER*</label>
        <input id="donationCardNumber" type="text" class="form-control" formControlName="cardNumber"
          inputmode="numeric" autocomplete="cc-number" placeholder="0000 0000 0000 0000"
          [class.is-invalid]="invalid('cardNumber')">
        @if (invalid('cardNumber')) {
        <div class="text-danger small">Card number is required.</div>
        }
      </div>
      <div class="col-5">
        <label class="form-label small fw-medium" for="donationExp">EXPIRES*</label>
        <input id="donationExp" type="text" class="form-control" formControlName="exp"
          inputmode="numeric" autocomplete="cc-exp" placeholder="MM/YYYY" maxlength="7"
          [class.is-invalid]="invalid('exp')">
        @if (invalid('exp')) {
        <div class="text-danger small">Use MM/YYYY.</div>
        }
      </div>
      <div class="col-3">
        <label class="form-label small fw-medium" for="donationCvc">CVC*</label>
        <input id="donationCvc" type="text" class="form-control" formControlName="cvc"
          inputmode="numeric" autocomplete="cc-csc" maxlength="4" [class.is-invalid]="invalid('cvc')">
        @if (invalid('cvc')) {
        <div class="text-danger small">Required.</div>
        }
      </div>
      <div class="col-4">
        <label class="form-label small fw-medium" for="donationZip">ZIP*</label>
        <input id="donationZip" type="text" class="form-control" formControlName="zip"
          inputmode="numeric" autocomplete="postal-code" [class.is-invalid]="invalid('zip')">
        @if (invalid('zip')) {
        <div class="text-danger small">Required.</div>
        }
      </div>
    </div>

    @if (captchaEnabled) {
    <div class="mt-3">
      <app-turnstile [action]="turnstileAction" (outToken)="onTurnstileToken($event)"></app-turnstile>
      @if (awaitingCaptcha()) {
      <!-- Without this the Donate button is simply dead while Cloudflare decides, with nothing
           on screen to say why — and the check is invisible until it chooses to ask. -->
      @if (captchaStalled()) {
      <p class="text-danger small text-center mt-2 mb-0">
        We could not run the security check. Please reload the page, or disable any ad or
        privacy blocker for this site and try again.
      </p>
      } @else {
      <p class="text-muted small text-center mt-2 mb-0">Verifying you are not a robot…</p>
      }
      }
    </div>
    }
    }

  </form>

  @if (errorText(); as error) {
  <div class="alert alert-warning py-2 small mt-3 mb-0" role="alert">{{ error }}</div>
  }

  <div class="modal-action-row has-btn-2 mt-4">
    <button type="button" class="btn btn-outline-secondary" (click)="close()" [disabled]="isSubmitting()">
      Cancel
    </button>
    <button type="button" class="btn btn-secondary" (click)="donate()" [disabled]="!canSubmit()">
      @if (isSubmitting()) {
      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      Processing…
      } @else {
      Donate {{ displayAmount() }}
      }
    </button>
  </div>
  }
</div>
